import React, { useContext, useEffect, useRef, useState } from 'react';
import classnames from 'clsx';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import { useClickOutside } from '../../../../../../providers/useClickOutside/useClickOutside';
import styles from '../button.scss';
import StylesContext from '../../../StylesContext';
import semanticClassNames from '../../../../RichTextBox.semanticClassNames';
import AlignButton from './AlignButton';
import type { AlignButtonType } from './common';
import {
  alignmentButtons,
  getActiveAlignment,
  preventBubblingUp,
  getStateWithNewTextAlign,
  alignTypes,
} from './common';
import type { ExtendedToolbarChildrenProps } from '../../types';

export default (props: ExtendedToolbarChildrenProps) => {
  const [showOptions, setShowOptions] = useState(false);

  const activeAlignRef = useRef<HTMLButtonElement>(null);
  const leftAlignRef = useRef<SVGSVGElement>(null);
  const centerAlignRef = useRef<SVGSVGElement>(null);
  const rightAlignRef = useRef<SVGSVGElement>(null);
  const justifyAlignRef = useRef<SVGSVGElement>(null);

  const hideOptions = () => {
    setShowOptions(false);
  };

  const stylesContext = useContext(StylesContext);

  useClickOutside(
    [
      activeAlignRef,
      leftAlignRef,
      centerAlignRef,
      rightAlignRef,
      justifyAlignRef,
    ],
    hideOptions,
  );

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      activeAlignRef.current?.focus();
      setShowOptions(false);
    }
  };

  useEffect(() => {
    if (showOptions) {
      leftAlignRef.current?.focus();
    }
  }, [showOptions]);

  const { setEditorState, getEditorState, rootId } = props;

  const alignRefs = {
    left: leftAlignRef,
    right: rightAlignRef,
    center: centerAlignRef,
    justify: justifyAlignRef,
  };

  const changeTextAlign = (
    e: React.MouseEvent<HTMLOrSVGElement>,
    newAlignment: AlignButtonType,
  ) => {
    preventBubblingUp(e);

    const newState = getStateWithNewTextAlign(newAlignment, getEditorState());
    hideOptions();
    setEditorState(newState);
  };

  const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    preventBubblingUp(event);
    setShowOptions(true);
  };

  const activeAlignment = getActiveAlignment(getEditorState());
  return (
    <div
      onKeyDown={onKeyDown}
      className={styles.buttonContainer}
      onMouseDown={preventBubblingUp}
    >
      <button
        ref={activeAlignRef}
        className={classnames(styles.button, {
          [styles.hovered]: stylesContext.hovered,
          [styles.focused]: stylesContext.focused,
          [styles.error]: stylesContext.error,
        })}
        onClick={onClickHandler}
        type="button"
        aria-haspopup="true"
        aria-expanded={showOptions}
        aria-controls={`alignMenu_${rootId}`}
      >
        <div
          className={classnames(
            styles.buttonContent,
            formatClassNames(semanticClassNames.icons),
          )}
        >
          <AlignButton tabIndex={-1}>
            {alignmentButtons[activeAlignment]}
          </AlignButton>
        </div>
        {showOptions && (
          <div
            role="menu"
            id={`alignMenu_${rootId}`}
            className={classnames(
              styles.menuContainer,
              {
                [styles.hovered]: stylesContext.hovered,
                [styles.focused]: stylesContext.focused,
                [styles.error]: stylesContext.error,
              },
              styles.inline,
            )}
          >
            {alignTypes.map(alignType => {
              return (
                <button
                  onClick={e => changeTextAlign(e, alignType)}
                  key={alignType}
                >
                  <AlignButton
                    selected={activeAlignment === alignType}
                    ref={alignRefs[alignType]}
                  >
                    {alignmentButtons[alignType]}
                  </AlignButton>
                </button>
              );
            })}
          </div>
        )}
      </button>
    </div>
  );
};
